import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dealsApi, marketsApi } from '../services/api';
import { DealForm } from '../components/DealForm';
import type { DiscountDeal, DealCreateRequest, Market, MarketCreateRequest, MarketUpdateRequest } from 'shared';

type MarketType = 'major' | 'minor';

const MARKET_TYPE_COLORS: Record<MarketType, string> = {
  major: 'bg-purple-100 text-purple-800',
  minor: 'bg-blue-100 text-blue-800',
};

function formatDateRange(start: string, end: string) {
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (s.getFullYear() !== e.getFullYear()) {
    return `${s.toLocaleDateString('en-US', { ...opts, year: 'numeric' })} – ${e.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
  }
  return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
}

function addDays(dateStr: string, n: number) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

interface MarketFormProps {
  market?: Market;
  onSubmit: (data: MarketCreateRequest | MarketUpdateRequest) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

function MarketForm({ market, onSubmit, onCancel, isLoading }: MarketFormProps) {
  const [form, setForm] = useState({
    name: market?.name ?? '',
    market_type: (market?.market_type ?? 'major') as MarketType,
    start_date: market?.start_date ?? '',
    end_date: market?.end_date ?? '',
    notes: market?.notes ?? '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: form.name,
      market_type: form.market_type,
      start_date: form.start_date,
      end_date: form.end_date,
      notes: form.notes || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Market Name</label>
        <input
          type="text"
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g. Spring Market 2026"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
        <select
          value={form.market_type}
          onChange={(e) => setForm({ ...form, market_type: e.target.value as MarketType })}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="major">Major (Apr / Oct)</option>
          <option value="minor">Minor (Jan / Jul)</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
          <input
            type="date"
            required
            value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
          <input
            type="date"
            required
            value={form.end_date}
            onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
        <textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          rows={2}
          placeholder="Location, hotel, additional details…"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div className="flex gap-3 justify-end pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50">
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? 'Saving…' : market ? 'Update Market' : 'Add Market'}
        </button>
      </div>
    </form>
  );
}

function getStatusColor(status: string) {
  switch (status) {
    case 'active': return 'bg-green-100 text-green-800';
    case 'upcoming': return 'bg-blue-100 text-blue-800';
    case 'expired': return 'bg-gray-100 text-gray-800';
    case 'deactivated': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}

export function MarketsAndDeals() {
  const queryClient = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);

  // Market state
  const [showMarketForm, setShowMarketForm] = useState(false);
  const [editingMarket, setEditingMarket] = useState<Market | null>(null);

  // Deal state
  const [showDealForm, setShowDealForm] = useState(false);
  const [editingDeal, setEditingDeal] = useState<DiscountDeal | null>(null);

  const { data: markets = [], isLoading: marketsLoading } = useQuery({
    queryKey: ['markets'],
    queryFn: () => marketsApi.list(),
  });

  const { data: deals = [], isLoading: dealsLoading } = useQuery({
    queryKey: ['deals'],
    queryFn: () => dealsApi.list(),
  });

  // Market mutations
  const createMarket = useMutation({
    mutationFn: (data: MarketCreateRequest) => marketsApi.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['markets'] }); setShowMarketForm(false); },
  });

  const updateMarket = useMutation({
    mutationFn: ({ id, data }: { id: string; data: MarketUpdateRequest }) => marketsApi.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['markets'] }); setEditingMarket(null); },
  });

  const deleteMarket = useMutation({
    mutationFn: (id: string) => marketsApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['markets'] }),
  });

  // Deal mutations
  const createDeal = useMutation({
    mutationFn: (data: DealCreateRequest) => dealsApi.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['deals'] }); setShowDealForm(false); },
  });

  const deleteDeal = useMutation({
    mutationFn: (id: string) => dealsApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['deals'] }),
  });

  const upcoming = markets.filter((m) => m.end_date >= today).sort((a, b) => a.start_date.localeCompare(b.start_date));
  const past = markets.filter((m) => m.end_date < today).sort((a, b) => b.start_date.localeCompare(a.start_date));

  if (marketsLoading || dealsLoading) {
    return <div className="text-center py-8 text-gray-500">Loading…</div>;
  }

  return (
    <div className="space-y-8">

      {/* ── Markets ── */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Markets</h2>
            <p className="text-sm text-gray-500 mt-0.5">Major: Apr & Oct · Minor: Jan & Jul · Post-market travel window: 4 weeks</p>
          </div>
          <button
            onClick={() => setShowMarketForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
          >
            Add Market
          </button>
        </div>

        {upcoming.length === 0 && past.length === 0 ? (
          <p className="text-gray-500 text-sm">No markets added yet. Add your upcoming market dates so the scheduler can plan post-market travel weeks.</p>
        ) : null}

        {/* Upcoming / active markets */}
        {upcoming.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {upcoming.map((m) => {
              const isActive = m.start_date <= today && m.end_date >= today;
              const postEnd = addDays(m.end_date, 28);
              const inPostWindow = today > m.end_date && today <= postEnd;
              return (
                <div key={m.id} className="bg-white rounded-lg shadow p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-gray-900 leading-snug">{m.name}</h3>
                    <span className={`shrink-0 px-2 py-0.5 text-xs rounded-full font-medium ${MARKET_TYPE_COLORS[m.market_type as MarketType] ?? 'bg-gray-100 text-gray-700'}`}>
                      {m.market_type}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600">{formatDateRange(m.start_date, m.end_date)}</p>

                  {isActive && (
                    <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800 font-medium">In progress</span>
                  )}
                  {inPostWindow && (
                    <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800 font-medium">Post-market travel (until {postEnd})</span>
                  )}

                  {m.notes && <p className="text-xs text-gray-500">{m.notes}</p>}

                  <div className="flex gap-3 pt-1">
                    <button onClick={() => setEditingMarket(m)} className="text-sm text-blue-600 hover:text-blue-800">Edit</button>
                    <button
                      onClick={() => { if (confirm('Delete this market?')) deleteMarket.mutate(m.id); }}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Past markets (collapsed list) */}
        {past.length > 0 && (
          <details className="group">
            <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700 select-none list-none flex items-center gap-1">
              <svg className="w-4 h-4 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              {past.length} past market{past.length !== 1 ? 's' : ''}
            </summary>
            <div className="mt-2 space-y-1">
              {past.map((m) => (
                <div key={m.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-md text-sm">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${MARKET_TYPE_COLORS[m.market_type as MarketType] ?? 'bg-gray-100 text-gray-700'}`}>
                      {m.market_type}
                    </span>
                    <span className="font-medium text-gray-700">{m.name}</span>
                    <span className="text-gray-400">{formatDateRange(m.start_date, m.end_date)}</span>
                  </div>
                  <button
                    onClick={() => { if (confirm('Delete this market?')) deleteMarket.mutate(m.id); }}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </details>
        )}
      </section>

      <hr className="border-gray-200" />

      {/* ── Deals ── */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Discount Deals</h2>
          <button
            onClick={() => setShowDealForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
          >
            Add Deal
          </button>
        </div>

        {/* Active & Upcoming */}
        <div>
          <h3 className="text-base font-medium text-gray-700 mb-3">Active &amp; Upcoming</h3>
          {deals.filter((d) => ['active', 'upcoming'].includes(d.status)).length === 0 ? (
            <p className="text-sm text-gray-500">No active or upcoming deals</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {deals
                .filter((d) => ['active', 'upcoming'].includes(d.status))
                .map((deal) => (
                  <div key={deal.id} className="bg-white p-4 rounded-lg shadow">
                    <div className="flex items-start justify-between">
                      <h4 className="font-medium text-gray-900">{deal.name}</h4>
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${getStatusColor(deal.status)}`}>{deal.status}</span>
                    </div>
                    {deal.description && <p className="mt-2 text-sm text-gray-600">{deal.description}</p>}
                    <p className="mt-2 text-xs text-gray-500">{deal.start_date} – {deal.end_date}</p>
                    <div className="mt-3 flex gap-3">
                      <button onClick={() => setEditingDeal(deal)} className="text-sm text-blue-600 hover:text-blue-800">Edit</button>
                      <button onClick={() => deleteDeal.mutate(deal.id)} className="text-sm text-red-600 hover:text-red-800">Deactivate</button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Past Deals */}
        {deals.filter((d) => ['expired', 'deactivated'].includes(d.status)).length > 0 && (
          <details className="group">
            <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700 select-none list-none flex items-center gap-1">
              <svg className="w-4 h-4 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              {deals.filter((d) => ['expired', 'deactivated'].includes(d.status)).length} past deal{deals.filter((d) => ['expired', 'deactivated'].includes(d.status)).length !== 1 ? 's' : ''}
            </summary>
            <div className="mt-2 space-y-1">
              {deals.filter((d) => ['expired', 'deactivated'].includes(d.status)).map((deal) => (
                <div key={deal.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md text-sm">
                  <div>
                    <span className="font-medium text-gray-700">{deal.name}</span>
                    <span className="ml-2 text-gray-400">({deal.start_date} – {deal.end_date})</span>
                  </div>
                  <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${getStatusColor(deal.status)}`}>{deal.status}</span>
                </div>
              ))}
            </div>
          </details>
        )}
      </section>

      {/* ── Modals ── */}
      {showMarketForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Market</h3>
            <MarketForm
              onSubmit={(data) => createMarket.mutate(data as MarketCreateRequest)}
              onCancel={() => setShowMarketForm(false)}
              isLoading={createMarket.isPending}
            />
          </div>
        </div>
      )}

      {editingMarket && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Market</h3>
            <MarketForm
              market={editingMarket}
              onSubmit={(data) => updateMarket.mutate({ id: editingMarket.id, data })}
              onCancel={() => setEditingMarket(null)}
              isLoading={updateMarket.isPending}
            />
          </div>
        </div>
      )}

      {showDealForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Deal</h3>
            <DealForm
              onSubmit={(data) => createDeal.mutate(data)}
              onCancel={() => setShowDealForm(false)}
              isLoading={createDeal.isPending}
            />
          </div>
        </div>
      )}

      {editingDeal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Deal</h3>
            <DealForm
              deal={editingDeal}
              onSubmit={() => setEditingDeal(null)}
              onCancel={() => setEditingDeal(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
